import * as React from 'react';
import * as downloadFile from 'downloadjs';
import { Theme, StyleRules } from '@material-ui/core/styles';
import withStyles, { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  Button,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  TextField,
  InputLabel,
  NativeSelect,
  Tooltip,
} from '@material-ui/core';
import { GridSize } from '@material-ui/core/Grid';
import { grey } from '@material-ui/core/colors';
import classNames from 'classnames';
import {
  ComparisonSearch as ComparisonSearchType,
} from '../modules/comparisonSearch';
import { UploadFileForSearch, UploadFilePattern } from '../modules/uploadForSearch';
import { ApplyContent } from '../modules/apply';
import SnackbarsMessage from './SnackbarsMessage';
import 'react-tabs/style/react-tabs.css';
import './Comparison.css';

interface Props {
  classes: ClassNameMap;
  files: (UploadFileForSearch | null)[];
  filePatterns: UploadFilePattern[];
  goUploadSearchPage: () => void;
  // 比較関連
  compareSearchDocuments: (fileIds: number[], fileTypes: string[]) => void;
  clearComparisonMessages: () => void;
  comparisonSearch: ComparisonSearchType | null;
  messages: string[];
  status: number;
  // TSVダウンロード関連
  downloadComparisonDocuments: (fileIds: number[], fileTypes: string[]) => Promise<void>;
  clearComparisonDownloadedMessages: () => void;
  comparisonDownload: Promise<Blob> | null;
  comparisonDownloadedErrorMessages: string[];
  // 申請関連(apply)
  contents: ApplyContent[];
  successMessages: string[];
  errorMessages: string[];
  changeApplyContent: (content: ApplyContent) => void;
  changeCategory: (index: number, content: ApplyContent) => void;
  clearApplyContents: () => void;
  submitApplyContents: (name: string, contents: ApplyContent[]) => Promise<void>;
  clearSuccessMessages: () => void;
  clearErrorMessages: () => void;
}

interface State {
  modalIsOpen: boolean;
  searchWord: String;
  nextCount1: number;
  nextCount2: number;
  nextCount3: number;
}

const TooltipLarge = withStyles({
  tooltip: {
    fontSize: '13px',
    maxWidth: '100%',
  },
})(Tooltip);

const style = (theme: Theme): StyleRules => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing.unit * 2,
  },
  fileName: {
    wordBreak: 'break-word',
  },
  wrapper: {
    paddingLeft: theme.spacing.unit * 2,
    paddingRight: theme.spacing.unit * 2,
  },
  progress: {
    marginTop: theme.spacing.unit * 3,
    padding: theme.spacing.unit * 2,
  },
  divider: {
    backgroundColor: grey[600],
  },
  clickedItem: {
    backgroundColor: '#FFCCFF',
  },
  sameItem: {
    backgroundColor: grey[600],
  },
  check: {
    marginTop: '5px',
  },
  button: {
    // margin: theme.spacing.unit,
    position: 'absolute',
    left: 0,
    top: 20,
  },
  highlightContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'stretch',
  },
  highlightItem: {
    flex: 1,
    width: '100%',
    padding: 5,
    margin: 5,
  },
  searchBox: {
    width: '100%',
    padding: 5,
    margin: 5,
    position: 'relative',
  },
  topBox: {
    padding: 5,
  },
  fileNameTypography: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    margin: 5,
  },
  paperScroll: {
    width: '100%',
    height: '63vh',
    overflowY: 'scroll',
  },
  hiddenDoc: {
    display: 'none !important',
  },
});

class ComparisonSearch extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalIsOpen: false,
      searchWord: '',
      nextCount1: 0,
      nextCount2: 0,
      nextCount3: 0,
    };

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.highlightSearchWords = this.highlightSearchWords.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.nextSearchWords = this.nextSearchWords.bind(this);
    this.backUploadPage = this.backUploadPage.bind(this);
  }

  componentDidMount() {
    const { files, filePatterns, compareSearchDocuments, goUploadSearchPage } = this.props;
    const fileIds: number[] = [];
    const fileTypes: string[] = [];
    files.forEach((file, i) => {
      if (file) {
        fileIds.push(file.id);
        fileTypes.push(filePatterns[i]);
      }
    });
    if (fileIds.length > 0) {
      if (!this.props.comparisonSearch) {
        compareSearchDocuments(fileIds, fileTypes);
      }
    } else {
      goUploadSearchPage();
    }
  }

  // 指摘ダイアログ
  openModal() {
    this.setState({ modalIsOpen: true });
  }
  closeModal() {
    this.setState({ modalIsOpen: false });
  }

  backUploadPage() {
    this.props.files.splice(0);
    this.props.files.push(null);
    this.props.files.push(null);
    this.props.files.push(null);
    this.props.goUploadSearchPage();
  }

  // TSVダウンロード
  downloadTSV() {
    const { files, filePatterns, downloadComparisonDocuments } = this.props;
    const fileIds: number[] = [];
    const fileTypes: string[] = [];
    files.forEach((file, i) => {
      if (file) {
        fileIds.push(file.id);
        fileTypes.push(filePatterns[i]);
      }
    });
    if (fileIds.length > 0) {
      downloadComparisonDocuments(fileIds, fileTypes).then(() => {
        const { comparisonDownload } = this.props;
        if (comparisonDownload) {
          comparisonDownload.then((blob) => {
            downloadFile(blob, '比較結果.tsv');
          });
        }
      });
    }
  }

  isClickedItem(
    orgTagId: number,
    position: number,
    order: number,
    text: string,
  ) {
    const { contents } = this.props;
    const exists = contents.filter((value) => {
      return (
        value.orgTagId === orgTagId &&
        value.position === position &&
        value.order === order &&
        value.text === text
      );
    });
    return exists.length;
  }

  isSameItemInClickedItem(text: string) {
    let exists = false;
    const { contents } = this.props;
    for (const c of contents) {
      exists = (c.text === text);
      if (exists) break;
    }
    return exists;
  }

  handleChangeList(
    fileName: string,
    orgTagId: number,
    newTagId: number,
    position: number,
    order: number,
    text: string,
  ) {
    this.props.changeApplyContent({
      fileName,
      position,
      order,
      text,
      orgTagId,
      newTagId,
    });
  }

  // キーワード検索
  highlightSearchWords = () => {
    // キーワード取得
    const searchWord = this.state.searchWord;
    if (!searchWord || searchWord.match(/^[A-Za-z0-9]*$/)) {
      return;
    }

    // 次へボタン位置リセット
    this.setState({ nextCount1: 0 });
    this.setState({ nextCount2: 0 });
    this.setState({ nextCount3: 0 });

    const mainDocsObj = document.getElementsByClassName('pmainDoc');
    const mainDocsArray = Array.from(mainDocsObj);
    // let hiddonDoc = '';
    let index = 0;
    mainDocsArray.forEach((mainDoc, dotIndex) => {
      if (mainDoc.className.includes('hiddenDoc')) {
        // hiddonDoc = mainDoc.innerHTML;
      } else {
        // 前回のハイライトセット
        mainDoc.innerHTML = mainDoc.innerHTML.replace(new RegExp(/<span name="highlightSpan">/, 'g'), '');
        mainDoc.innerHTML = mainDoc.innerHTML.replace(new RegExp(/<\/span>/, 'g'), '');
        Array.from(mainDoc.getElementsByTagName('a')).forEach((hilightATag) => {
          if (hilightATag.parentNode && hilightATag.firstChild) {
            hilightATag.parentNode.replaceChild(hilightATag.firstChild, hilightATag);
          }
        });
        Array.from(mainDoc.getElementsByClassName('hilightStyle')).forEach((hilightSpanTag) => {
          if (hilightSpanTag.parentNode && hilightSpanTag.firstChild) {
            hilightSpanTag.parentNode.replaceChild(hilightSpanTag.firstChild, hilightSpanTag);
          }
        });

        // 今回のハイライト
        mainDoc.innerHTML = mainDoc.innerHTML.replace(new RegExp(searchWord.toString(), 'g'), `<span name="highlightSpan"><a name="highlightWord${index}" href="javascript:void(0);"><span class="hilightStyle">${searchWord.toString()}</span></a></span>`);

        const hilightWords = document.getElementsByName(`highlightWord${index}`);
        if (hilightWords && hilightWords.length > 0) {
          // 一番下にフォーカスあてる
          if (document.getElementsByName(`endButtonWord${dotIndex}`).length === 0) {
            mainDoc.insertAdjacentHTML('afterend', `<button name="endButtonWord${dotIndex}" href="javascript:void(0);" tabindex="0" style="background-color: transparent;border: none;"> </button>`);
          }
          document.getElementsByName(`endButtonWord${dotIndex}`)[0].focus();
          hilightWords[0].focus();
        }

        index += 1;
      }
    });
  }

  // 次へボタン
  nextSearchWords(index: number) {
    let count = 0;
    switch (index) {
      case 0:
        count = this.state.nextCount1;
        break;
      case 1:
        count = this.state.nextCount2;
        break;
      case 2:
        count = this.state.nextCount3;
        break;
    }
    if (document.getElementsByName(`highlightWord${index}`)[count] === undefined || document.getElementsByName(`highlightWord${index}`).length - 1 < count) {
      switch (index) {
        case 0:
          this.setState({ nextCount1: 0 });
          break;
        case 1:
          this.setState({ nextCount2: 0 });
          break;
        case 2:
          this.setState({ nextCount3: 0 });
          break;
      }
    } else {
      document.getElementsByName(`highlightWord${index}`)[count].focus();
      if (document.getElementsByName(`highlightWord${index}`)[count + 1] === undefined) {
        switch (index) {
          case 0:
            this.setState({ nextCount1: 0 });
            break;
          case 1:
            this.setState({ nextCount2: 0 });
            break;
          case 2:
            this.setState({ nextCount3: 0 });
            break;
        }
      } else {
        switch (index) {
          case 0:
            this.setState({ nextCount1: count + 1 });
            break;
          case 1:
            this.setState({ nextCount2: count + 1 });
            break;
          case 2:
            this.setState({ nextCount3: count + 1 });
            break;
        }
      }
    }
  }

  // 入力内容保持
  handleChangeSearchName = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    this.setState({ searchWord: value });
  }

  handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const mainDocsObj = document.getElementsByClassName('pmainDoc');
    const mainDocsArray = Array.from(mainDocsObj);
    let hiddonDoc = '';
    let isFirst = true;
    let index = 0;
    mainDocsArray.forEach((mainDoc, donIndex) => {
      if (mainDoc.className.includes('hiddenDoc')) {
        hiddonDoc = mainDoc.innerHTML;
      } else {
        // 前回のハイライトセット
        mainDoc.innerHTML = hiddonDoc;
        if (value === '99999') return;

        // 今回のハイライト
        const searchTagsObj = mainDoc.getElementsByClassName('searchTags');
        const searchTagsArray = Array.from(searchTagsObj);
        searchTagsArray.forEach((searchTag) => {
          // 前回のハイライトセット
          const currentTag = searchTag.getAttribute('tag');
          if (value === currentTag) {
            searchTag.classList.add('search_tag_chg');
            searchTag.insertAdjacentHTML('beforebegin', '<button name="hoge" href="javascript:void(0);" tabindex="0" style="background-color: transparent;border: none;"> </button>');
            if (isFirst) {
              // 一番下にフォーカスあてる
              mainDoc.insertAdjacentHTML('afterend', `<button name="endButton${donIndex}" href="javascript:void(0);" tabindex="0" style="background-color: transparent;border: none;"> </button>`);
              document.getElementsByName(`endButton${donIndex}`)[0].focus();

              document.getElementsByName('hoge')[index].focus();
              isFirst = false;
            }
            index += 1;
          } else {
            searchTag.classList.remove('search_tag_chg');
          }
        });
      }
      isFirst = true;
    });
  }

  render() {
    const {
      classes,
      comparisonSearch,
      messages,
      status,
      successMessages,
      errorMessages,
      clearSuccessMessages,
      clearErrorMessages,
      comparisonDownloadedErrorMessages,
      clearComparisonDownloadedMessages,
    } = this.props;

    const xs: GridSize | undefined = comparisonSearch ?
      (Math.floor(12 / comparisonSearch.documents.length) as GridSize) :
      undefined;

    // TODO: 暫定
    const EXCLUDE_CATEGORY = '9999';

    // tokenチェックエラーの場合、トークンを削除し、画面遷移を発生させ、ログインページに遷移
    if (status === 403 || status === 401) {
      document.cookie = 'access_token=; expires=0';
      document.cookie = 'username=; expires=0';
      this.backUploadPage();
    }

    return (
      <React.Fragment>
        <SnackbarsMessage
          variant="success"
          messages={successMessages}
          onClose={clearSuccessMessages}
        />
        <SnackbarsMessage
          variant="error"
          messages={errorMessages}
          onClose={clearErrorMessages}
        />
        <SnackbarsMessage
          variant="error"
          messages={comparisonDownloadedErrorMessages}  /* FIXME: 共通化したい */
          onClose={clearComparisonDownloadedMessages}
        />
        {comparisonSearch ? (
          <Grid
            container
            className={classNames(classes.root)}
            spacing={16}>
            <Grid container alignItems="flex-end" justify="center" className={classes.searchBox}>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={this.backUploadPage}>
                戻る
              </Button>
              {/* タグセレクトBOX */}
              <Grid item className={classes.topBox}>
                <InputLabel htmlFor="select" className={classes.topBox}>タグ:</InputLabel>
                <NativeSelect id="select" onChange={this.handleChange}>
                  <option value="99999" selected></option>
                  {comparisonSearch && Object.keys(comparisonSearch.tags).filter((k) => {
                    if (k === EXCLUDE_CATEGORY) return false;
                    return true;
                  }).map((k, i) => (
                    <option value={k}>{comparisonSearch.tags[k]}</option>
                  ))}
                </NativeSelect>
              </Grid>
              {/* 検索ボックス */}
              <Grid item className={classes.topBox}>
                <TextField label="キーワード入力" id="searchWord" onChange={this.handleChangeSearchName} />
              </Grid>
              {/* 検索ボタン */}
              <Grid item className={classes.topBox}>
                <Button variant="contained" color="primary" onClick={this.highlightSearchWords}>検索</Button>
              </Grid>
            </Grid>
            {/* 比較本文 */}
            <Grid container justify="center" className="cardContainer">
              {comparisonSearch && comparisonSearch.documents.map((k, i) => (
                <Grid item xs={xs} className={classNames(classes.topBox, 'mainDoc')}>
                  <Grid container justify="center" className="cardContainer">
                    <Grid item xs={10} justify="center">
                      <TooltipLarge title={k.fileName}>
                        <Typography
                          variant="headline"
                          className={classes.fileNameTypography}>{k.fileName}</Typography>
                      </TooltipLarge>
                    </Grid>
                    <Grid item xs={2} justify="center">
                      {/* 検索ボタン */}
                      <Button variant="contained" color="primary" onClick={this.nextSearchWords.bind(this, i)}>次へ</Button>
                    </Grid>
                  </Grid>
                  <Grid container justify="center" className="cardContainer">
                    <Paper className={classNames(classes.paper, classes.paperScroll)}>
                      <p dangerouslySetInnerHTML={{ __html: k.mainDoc }} className={classNames('pmainDoc', classes.hiddenDoc, 'hiddenDoc')} />
                      <p dangerouslySetInnerHTML={{ __html: k.mainDoc }} className={'pmainDoc'} />
                    </Paper>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : (messages.length === 0 &&
          <div className={classes.root}>
            <LinearProgress className={classes.progress} />
          </div>
          )
        }
      </React.Fragment>
    );
  }
}

export default withStyles(style)(ComparisonSearch);



// WEBPACK FOOTER //
// ./src/components/ComparisonSearch.tsx